import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Divider,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery } from '@apollo/client';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ChecklistTemplateVersionBump, RevisionType } from '../__generated__/graphql';
import { StyledPaper } from '../theme';
import { formatDateOnly } from '../Formatters';
import LoadingSpinner from '../LoadingSpinner';
import { revisionTypeToDisplayText, revisionTypeToShortText } from './RevisionModel';
import {
  checklistTitle,
  duplicateCheckItems,
  EditableCheckItem,
  EditableList,
  EditablePhase,
  EditableTask,
  editableRevisionTypes,
  formatWeightTotal,
  hasChanges,
  move,
  newCheckItem,
  newKey,
  newTask,
  nextVersion,
  parseWeight,
  phaseOrderConflicts,
  removeAt,
  replaceAt,
  tasksWithInvalidWeight,
  toEditableLists,
  toPublishInput,
  typesWithoutTasks,
  weightTotal,
} from './checklistTemplateModel';
import { GET_CHECKLIST_TEMPLATE, GET_CHECKLIST_VERSIONS_IN_USE, PUBLISH_CHECKLIST_TEMPLATE_VERSION } from './checklistTemplate.graphql';
import { ChecklistVersionMigration } from './ChecklistVersionMigration';
import { useCanEditChecklists } from './useCanEditChecklists';

export const ChecklistTemplateEditor: FC = () => {
  const { data, loading } = useQuery(GET_CHECKLIST_TEMPLATE, { fetchPolicy: 'cache-and-network' });
  const canEdit = useCanEditChecklists();
  const template = data?.checklistTemplate;

  const [searchParams] = useSearchParams();
  const [lists, setLists] = useState<EditableList[]>([]);
  const [selectedType, setSelectedType] = useState(Math.max(0, editableRevisionTypes.indexOf(searchParams.get('type') as RevisionType)));
  const checklistHeading = useRef<HTMLDivElement>(null);
  const scrollToChecklist = searchParams.get('section') === 'checklist';
  const cameFromRevision = searchParams.get('revision');
  const [bump, setBump] = useState(ChecklistTemplateVersionBump.Minor);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const [publish, { loading: publishing }] = useMutation(PUBLISH_CHECKLIST_TEMPLATE_VERSION, {
    refetchQueries: [{ query: GET_CHECKLIST_VERSIONS_IN_USE }],
  });

  useEffect(() => setLists(toEditableLists(template)), [template]);

  const hasContent = lists.some((x) => x.phases.length > 0 || x.checkItems.length > 0);
  useEffect(() => {
    if (scrollToChecklist && hasContent) checklistHeading.current?.scrollIntoView({ block: 'start' });
  }, [scrollToChecklist, hasContent]);

  const dirty = useMemo(() => hasChanges(lists, template), [lists, template]);
  const missingTasks = useMemo(() => typesWithoutTasks(lists), [lists]);
  const invalidWeights = useMemo(() => tasksWithInvalidWeight(lists), [lists]);
  const duplicates = useMemo(() => duplicateCheckItems(lists), [lists]);
  const orderConflicts = useMemo(() => phaseOrderConflicts(lists), [lists]);
  const versionAfterMinor = nextVersion(template?.version, ChecklistTemplateVersionBump.Minor);
  const versionAfterMajor = nextVersion(template?.version, ChecklistTemplateVersionBump.Major);

  const list = lists[selectedType];
  const updatePhases = (phases: EditablePhase[]) => setLists(replaceAt(lists, selectedType, { ...list, phases }));
  const updateCheckItems = (checkItems: EditableCheckItem[]) => setLists(replaceAt(lists, selectedType, { ...list, checkItems }));

  const handlePublish = async () => {
    setError(null);
    const result = await publish({
      variables: { input: { ...toPublishInput(lists), bump } },
    });

    const failure = result.data?.checklistTemplatePublishVersion?.errors?.[0];
    if (failure) {
      setError('message' in failure ? failure.message : 'Kunne ikke publisere sjekklisten.');
      return;
    }

    setBump(ChecklistTemplateVersionBump.Minor);
  };

  if (loading && !template) return <LoadingSpinner />;

  return (
    <Box sx={{ maxWidth: (theme) => theme.breakpoints.values.xl, mx: 'auto' }}>
      <StyledPaper>
        {cameFromRevision && (
          <Link
            component={RouterLink}
            to={`/revision/${cameFromRevision}`}
            variant="body2"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <ArrowBackIcon fontSize="small" />
            Tilbake til revisjonen
          </Link>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" spacing={2}>
          <Box>
            <Typography variant="h5" component="h2">
              Aktivitetslister og sjekklister
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Versjon {template?.version}
              {template?.publishedAt && ` · publisert ${formatDateOnly(template.publishedAt)}`}
              {template?.publishedByUser?.name && ` av ${template.publishedByUser.name}`}
              {(template?.previousVersions?.length ?? 0) > 0 && (
                <>
                  {' · '}
                  <Link component="button" type="button" variant="caption" underline="hover" onClick={() => setShowHistory(!showHistory)}>
                    {showHistory ? 'Skjul tidligere versjoner' : `Vis ${template!.previousVersions!.length} tidligere versjoner`}
                  </Link>
                </>
              )}
            </Typography>
            <Collapse in={showHistory}>
              <Stack sx={{ mt: 0.5 }}>
                {template?.previousVersions
                  ?.filter((x) => !!x)
                  .map((previous) => (
                    <Typography key={previous.version} variant="caption" color="textSecondary">
                      Versjon {previous.version} · {formatDateOnly(previous.publishedAt)}
                      {previous.publishedByUser?.name && ` · ${previous.publishedByUser.name}`}
                    </Typography>
                  ))}
              </Stack>
            </Collapse>
          </Box>
          {canEdit && (
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={bump}
                onChange={(_event, value) => value && setBump(value)}
                aria-label="Hvor stor endringen er"
                sx={{ flexWrap: 'wrap' }}>
                <ToggleButton value={ChecklistTemplateVersionBump.Minor}>Mindre endring{versionAfterMinor && ` · ${versionAfterMinor}`}</ToggleButton>
                <ToggleButton value={ChecklistTemplateVersionBump.Major}>
                  Ny hovedversjon{versionAfterMajor && ` · ${versionAfterMajor}`}
                </ToggleButton>
              </ToggleButtonGroup>
              <Button color="secondary" disabled={!dirty || publishing} onClick={() => setLists(toEditableLists(template))}>
                Forkast
              </Button>
              <Button
                variant="contained"
                disabled={
                  !dirty || publishing || missingTasks.length > 0 || invalidWeights.length > 0 || duplicates.length > 0 || orderConflicts.length > 0
                }
                onClick={handlePublish}>
                Publiser
              </Button>
            </Stack>
          )}
        </Stack>

        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          En ny versjon gjelder bare for revisjoner som startes etterpå. Revisjoner som er i gang beholder sin egen sjekkliste.
        </Typography>

        {!canEdit && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Du kan se malen, men ikke endre den.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {invalidWeights.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Andel fremdrift må være et tall, 0 eller mer: {invalidWeights.join(', ')}.
          </Alert>
        )}
        {duplicates.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Står mer enn én gang i samme sjekkliste: {duplicates.join(', ')}. Et punkt kjennes igjen på navnet, så navnene må være ulike.
          </Alert>
        )}
        {orderConflicts.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Alle revisjonstyper får fasene i samme rekkefølge, satt av listene fra {revisionTypeToDisplayText(editableRevisionTypes[0])} og bortover.
            Dette stemmer ikke med den:
            <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
              {orderConflicts.map((conflict) => (
                <li key={`${conflict.type}-${conflict.before}-${conflict.after}`}>
                  {revisionTypeToDisplayText(conflict.type)} har «{conflict.before}» før «{conflict.after}», men revisjonene viser «{conflict.after}»
                  først.
                </li>
              ))}
            </Box>
          </Alert>
        )}
        {missingTasks.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Disse revisjonstypene har ingen aktiviteter: {missingTasks.map(revisionTypeToShortText).join(', ')}. Alle typer må ha minst én.
          </Alert>
        )}

        <Tabs value={selectedType} onChange={(_event, value) => setSelectedType(value)} variant="scrollable" sx={{ mt: 2, mb: 1 }}>
          {editableRevisionTypes.map((type) => (
            <Tab key={type} label={revisionTypeToDisplayText(type)} />
          ))}
        </Tabs>

        {list && (
          <Stack spacing={2}>
            <Section
              title={`Aktiviteter (${countTasks(list)})`}
              description="Fasene blir milepælene i boksen Aktiviteter. Andel fremdrift sier hvor stor del av arbeidet hver aktivitet er."
              extra={<WeightTotal total={weightTotal(list.phases)} />}>
              <Stack spacing={2} divider={<Divider />}>
                {list.phases.map((phase, phaseIndex) => (
                  <PhaseEditor
                    key={phase.key}
                    phase={phase}
                    readonly={!canEdit}
                    first={phaseIndex === 0}
                    last={phaseIndex === list.phases.length - 1}
                    onChange={(changed) => updatePhases(replaceAt(list.phases, phaseIndex, changed))}
                    onMove={(delta) => updatePhases(move(list.phases, phaseIndex, delta))}
                    onRemove={() => updatePhases(removeAt(list.phases, phaseIndex))}
                  />
                ))}
                {canEdit && (
                  <Box>
                    <Button startIcon={<AddIcon />} onClick={() => updatePhases([...list.phases, { key: newKey(), name: '', tasks: [] }])}>
                      Legg til fase
                    </Button>
                  </Box>
                )}
              </Stack>
            </Section>

            <Section
              ref={checklistHeading}
              title={`${checklistTitle} (${list.checkItems.filter((item) => !!item.name.trim()).length})`}
              description={`Punkter som krysses av i boksen ${checklistTitle}, uten faser og uten å telle med i fremdriften. Kan stå tom, og da vises ikke boksen.`}>
              <CheckItemListEditor items={list.checkItems} readonly={!canEdit} onChange={updateCheckItems} />
            </Section>
          </Stack>
        )}
      </StyledPaper>

      <ChecklistVersionMigration template={template} canEdit={canEdit} />
    </Box>
  );
};

const Section = React.forwardRef<HTMLDivElement, { title: string; description: string; extra?: React.ReactNode; children: React.ReactNode }>(
  ({ title, description, extra, children }, ref) => (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Box sx={{ mb: 2, scrollMarginTop: 80 }} ref={ref}>
        <Typography variant="h6" component="h3">
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
        {extra}
      </Box>
      {children}
    </Box>
  ),
);
Section.displayName = 'Section';

const PhaseEditor: FC<{
  phase: EditablePhase;
  readonly: boolean;
  first: boolean;
  last: boolean;
  onChange: (phase: EditablePhase) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}> = ({ phase, readonly, first, last, onChange, onMove, onRemove }) => {
  const setTasks = (tasks: EditablePhase['tasks']) => onChange({ ...phase, tasks });

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          variant="standard"
          placeholder="Fasenavn"
          value={phase.name}
          disabled={readonly}
          onChange={(event) => onChange({ ...phase, name: event.target.value })}
          slotProps={{ input: { style: { fontWeight: 500 } } }}
        />
        <Typography variant="caption" color="textSecondary" noWrap sx={{ flexShrink: 0 }}>
          Andel {formatWeightTotal(weightTotal([phase]))}
        </Typography>
        {!readonly && <RowActions first={first} last={last} removeTitle="Slett fasen og aktivitetene i den" onMove={onMove} onRemove={onRemove} />}
      </Stack>

      <Stack spacing={0.5} sx={{ mt: 1, ml: 2 }}>
        {phase.tasks.map((task, taskIndex) => (
          <TaskEditor
            key={task.key}
            task={task}
            readonly={readonly}
            first={taskIndex === 0}
            last={taskIndex === phase.tasks.length - 1}
            onChange={(changed) => setTasks(replaceAt(phase.tasks, taskIndex, changed))}
            onMove={(delta) => setTasks(move(phase.tasks, taskIndex, delta))}
            onRemove={() => setTasks(removeAt(phase.tasks, taskIndex))}
          />
        ))}
        {!readonly && (
          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setTasks([...phase.tasks, newTask()])}>
              Legg til aktivitet
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

const CheckItemListEditor: FC<{ items: EditableCheckItem[]; readonly: boolean; onChange: (items: EditableCheckItem[]) => void }> = ({
  items,
  readonly,
  onChange,
}) => (
  <Stack spacing={0.5}>
    {items.map((item, index) => (
      <TaskEditor
        key={item.key}
        task={item}
        readonly={readonly}
        first={index === 0}
        last={index === items.length - 1}
        onChange={(changed) => onChange(replaceAt(items, index, changed))}
        onMove={(delta) => onChange(move(items, index, delta))}
        onRemove={() => onChange(removeAt(items, index))}
      />
    ))}
    {!readonly && (
      <Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...items, newCheckItem()])}>
          Legg til punkt
        </Button>
      </Box>
    )}
  </Stack>
);

function TaskEditor<T extends EditableCheckItem | EditableTask>({
  task,
  readonly,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  task: T;
  readonly: boolean;
  first: boolean;
  last: boolean;
  onChange: (task: T) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <TextField
        sx={{ flexGrow: 1, minWidth: 220 }}
        size="small"
        variant="standard"
        placeholder="Aktivitet"
        value={task.name}
        disabled={readonly}
        onChange={(event) => onChange({ ...task, name: event.target.value })}
      />
      {'weight' in task && (
        <Tooltip title="Andel fremdrift: hvor stor del av arbeidet aktiviteten er">
          <TextField
            sx={{ width: 64, flexShrink: 0 }}
            size="small"
            variant="standard"
            value={task.weight}
            disabled={readonly}
            error={parseWeight(task.weight) === null}
            onChange={(event) => onChange({ ...task, weight: event.target.value })}
            slotProps={{ htmlInput: { inputMode: 'decimal', 'aria-label': 'Andel fremdrift', style: { textAlign: 'right' } } }}
          />
        </Tooltip>
      )}
      <Tooltip title="Om punktet kan lukkes som «Ikke relevant»">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={task.allowNotRelevant}
              disabled={readonly}
              onChange={(event) => onChange({ ...task, allowNotRelevant: event.target.checked })}
            />
          }
          label={<Typography variant="caption">Ikke relevant</Typography>}
          sx={{ flexShrink: 0, mr: 0 }}
        />
      </Tooltip>
      <Tooltip title="Om en begrunnelse må fylles ut når punktet merkes som ikke relevant">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={task.requireCommentWhenNotRelevant}
              disabled={readonly || !task.allowNotRelevant}
              onChange={(event) => onChange({ ...task, requireCommentWhenNotRelevant: event.target.checked })}
            />
          }
          label={<Typography variant="caption">Krev begrunnelse</Typography>}
          sx={{ flexShrink: 0, mr: 0 }}
        />
      </Tooltip>
      {!readonly && (
        <RowActions
          first={first}
          last={last}
          removeTitle={'weight' in task ? 'Slett aktiviteten' : 'Slett punktet'}
          onMove={onMove}
          onRemove={onRemove}
        />
      )}
    </Stack>
  );
}

const RowActions: FC<{ first: boolean; last: boolean; removeTitle: string; onMove: (delta: number) => void; onRemove: () => void }> = ({
  first,
  last,
  removeTitle,
  onMove,
  onRemove,
}) => (
  <Stack direction="row" sx={{ flexShrink: 0 }}>
    <IconButton size="small" disabled={first} onClick={() => onMove(-1)} title="Flytt opp">
      <ArrowUpwardIcon fontSize="small" />
    </IconButton>
    <IconButton size="small" disabled={last} onClick={() => onMove(1)} title="Flytt ned">
      <ArrowDownwardIcon fontSize="small" />
    </IconButton>
    <Tooltip title={removeTitle}>
      <IconButton size="small" onClick={onRemove}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Stack>
);

const WeightTotal: FC<{ total: number }> = ({ total }) => (
  <Typography variant="body2" color={Math.abs(total - 100) < 0.001 ? 'textSecondary' : 'warning.main'} sx={{ mt: 0.5 }}>
    Sum andel fremdrift: {formatWeightTotal(total)}
    {Math.abs(total - 100) >= 0.001 && ' – listene er laget for å gå opp i 100. Fremdriften regnes likevel ut av summen som står her.'}
  </Typography>
);

const countTasks = (list: EditableList | undefined) => list?.phases.reduce((total, phase) => total + phase.tasks.length, 0) ?? 0;
